import { Group, Modal, NumberInput, Stack } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import { useApi } from 'api/api-context';
import { DeviceAdminSetRemoteSourceResponse } from 'api/actions/device-admin-set-remote-source/device-admin-set-remote-source-response';
import Button from 'components/Button';
import { RemoteSourceSelect } from 'components/selects/RemoteSourceSelect';
import panic from 'errors/panic';
import { useEffect } from 'react';
import { SUCCESS_NOTIFICATION_COLOR } from 'utils/constants';

export interface ChangeDeviceRemoteSourceModalProps {
  deviceId: number;
  deviceName: string;
  remoteSourceId?: number;
  externalId?: string;
  opened: boolean;
  onClose: () => void;
  onRemoteSourceChange: (response: DeviceAdminSetRemoteSourceResponse) => void;
}

interface FormValues {
  remoteSourceId: string | null;
  externalId: number | '';
}

/**
 * Parses an existing external ID to a numeric form value when possible.
 */
function parseInitialExternalId(externalId?: string): number | '' {
  if (!externalId) {
    return '';
  }

  const parsed = Number(externalId);

  return Number.isFinite(parsed) ? parsed : '';
}

/**
 * Modal for moving a device to a selected remote source.
 */
export default function ChangeDeviceRemoteSourceModal({
  deviceId,
  deviceName,
  remoteSourceId,
  externalId,
  opened,
  onClose,
  onRemoteSourceChange,
}: ChangeDeviceRemoteSourceModalProps) {
  const { getAction } = useApi();
  const [loading, { open: startLoading, close: stopLoading }] = useDisclosure(false);
  const deviceAdminSetRemoteSource = getAction('DeviceAdminSetRemoteSource');

  const form = useForm<FormValues>({
    initialValues: {
      remoteSourceId: remoteSourceId ? String(remoteSourceId) : null,
      externalId: parseInitialExternalId(externalId),
    },
    validate: {
      remoteSourceId: (value) => (value ? null : 'Pole je povinné.'),
      externalId: (value) =>
        value === '' || value === null || Number.isNaN(Number(value)) ? 'Pole je povinné.' : null,
    },
  });

  /**
   * Resets the form whenever the modal is opened with current row values.
   */
  useEffect(() => {
    if (!opened) {
      return;
    }

    form.setValues({
      remoteSourceId: remoteSourceId ? String(remoteSourceId) : null,
      externalId: parseInitialExternalId(externalId),
    });
    form.resetDirty();
    form.clearErrors();
  }, [opened, remoteSourceId, externalId]);

  /**
   * Persists the selected remote source and external identifier for the device.
   */
  const submit = async (values: FormValues) => {
    if (!values.remoteSourceId || values.externalId === '') {
      /* istanbul ignore next -- explicitly authorized unreachable coverage blocker */
      return;
    }

    try {
      startLoading();

      const response = await deviceAdminSetRemoteSource({
        parameters: { deviceId: String(deviceId) },
        payload: {
          remoteSourceId: Number(values.remoteSourceId),
          externalId: String(values.externalId),
        },
      });

      onRemoteSourceChange(response);

      showNotification({
        title: 'Externý zdroj bol zmenený',
        message: `Externý zdroj zariadenia ${deviceName} bol úspešne upravený.`,
        color: SUCCESS_NOTIFICATION_COLOR,
      });

      onClose();
    } catch (error: any) {
      panic(error);
    } finally {
      stopLoading();
    }
  };

  return (
    <Modal
      centered
      opened={opened}
      onClose={onClose}
      title={`Zmeniť externý zdroj: ${deviceName}`}
      closeOnClickOutside={!loading}
      closeOnEscape={!loading}
      closeButtonProps={{ loading }}
    >
      <form onSubmit={form.onSubmit(submit)}>
        <Stack spacing={24}>
          <RemoteSourceSelect
            size="md"
            label="Externý zdroj"
            placeholder="Vyberte externý zdroj"
            disabled={loading}
            showDiscarded
            clearable={false}
            {...form.getInputProps('remoteSourceId')}
          />
          <NumberInput
            size="md"
            label="Externé ID"
            placeholder="Vyplňte externé ID"
            hideControls
            min={0}
            disabled={loading}
            {...form.getInputProps('externalId')}
          />
          <Group position="right" spacing={16}>
            <Button variant="subtle-gray" onClick={onClose} disabled={loading}>
              Zrušiť
            </Button>
            <Button type="submit" variant="primary" loading={loading}>
              Uložiť
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
